import { Dispatch, useState } from 'react';
import {
  Card,
  CardHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Skeleton,
  Text,
} from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import { EditorComponent } from '@frontend/components/EditorJS/EditorComponent';
import { InferenceTypeSelector } from '@frontend/components/InferenceTypeSelector';
import { TiptapEditorWrapper } from '@frontend/components/TiptapEditor';
import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useOpalUser } from '@frontend/hooks';
import * as agentStyles from '@frontend/styles/form.css';
import { SpecializedAgent } from '@frontend/types';

import { AgentFormFiles } from './AgentFormFiles';
import { styles } from './AgentFormInput.css';
import { ToolsInput } from './ToolsInput';
import { VariableInput } from './VariableInput';

type AgentFormInputProps = {
  formData: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shouldLoadPromptTemplate: boolean;
  shouldShowUnusedParamsWarning: boolean;
};

export function AgentFormInput({
  formData,
  dispatch,
  open,
  onOpenChange,
  shouldLoadPromptTemplate,
  shouldShowUnusedParamsWarning,
}: AgentFormInputProps) {
  const parameters = formData.parameters || [];
  const agentToolNames: string[] = formData.enabled_tools || [];

  const [decision] = useDecision('tiptap-editor');

  const { opalUser } = useOpalUser();
  const [promptError, setPromptError] = useState<string | null>(null);

  return (
    <Flex className={styles.container}>
      <Card bg="bg.secondary" px="8" py="4">
        <Disclosure w="full" onOpenChange={onOpenChange} open={open}>
          <CardHeader>
            <DisclosureTrigger>
              <Text className={styles.fieldTitle}>Input</Text>
            </DisclosureTrigger>
          </CardHeader>
          <DisclosureContent>
            <Flex pt="24" pb="20" px="24">
              <Field
                description="Place dynamic variables in double brackets, eg. [[variable_name]]."
                label="Prompt Template"
                info="Defines the prompt and core logic of this agent. This can include objectives, tasks, and output."
                error={promptError}
                required
                className={agentStyles.FormInputFieldMaxWidth}
              >
                {formData.prompt_template === undefined && shouldLoadPromptTemplate ? (
                  <Skeleton h="56" />
                ) : decision.enabled ? (
                  <TiptapEditorWrapper
                    placeholder="Enter your prompt template"
                    className={styles.promptTemplate}
                    value={formData.prompt_template || ''}
                    selectables={formData.parameters || []}
                    usedIn="agent-form"
                    onChange={(value: string) => {
                      dispatch({
                        type: 'UPDATE_FIELD',
                        field: 'prompt_template',
                        value,
                      });
                      if (!value || value.trim() === '') {
                        setPromptError('Prompt Template is required.');
                      } else {
                        setPromptError(null);
                      }
                    }}
                  />
                ) : (
                  <EditorComponent
                    placeholder="Enter your prompt template"
                    className={styles.promptTemplate}
                    value={formData.prompt_template || ''}
                    selectables={formData.parameters || []}
                    onChange={(value: string) => {
                      dispatch({
                        type: 'UPDATE_FIELD',
                        field: 'prompt_template',
                        value,
                      });
                      if (!value || value.trim() === '') {
                        setPromptError('Prompt Template is required.');
                      } else {
                        setPromptError(null);
                      }
                    }}
                  />
                )}
              </Field>
              <VariableInput
                parameters={parameters}
                dispatch={dispatch}
                promptTemplate={formData.prompt_template}
                shouldShowUnusedParamsWarning={shouldShowUnusedParamsWarning}
              />
              <ToolsInput
                agentToolNames={agentToolNames}
                dispatch={dispatch}
                opalInstanceId={opalUser.instance.instance_id}
              />
              <InferenceTypeSelector
                value={formData.inference_type}
                onChange={inference_type =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'inference_type', value: inference_type })
                }
              />
              <Field
                label="Creativity"
                info="(0-1 scale) controls the randomness of Opal’s output and how creative it should be. Lower values produce more predictable results, while higher values permit more variety."
              >
                <Flex flexDirection="row" gap="8">
                  <Input
                    placeholder="0"
                    value={formData.creativity?.toString() || '0'}
                    onChange={e => {
                      const value = parseFloat(e.target.value) || 0;
                      const clampedValue = Math.max(0, Math.min(1, value));
                      dispatch({ type: 'UPDATE_FIELD', field: 'creativity', value: clampedValue });
                    }}
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                  />
                </Flex>
              </Field>
              <Field label="Files" info="Attach reference files for your agent here">
                <AgentFormFiles formData={formData} dispatch={dispatch} />
              </Field>
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Card>
    </Flex>
  );
}
